// Namespaces
import * as ProposalNS from '../Proposal';

// Utils
import { buildCorrelationMatricesSections } from './ProposalCorrelationMatrices';
import { buildBmoGifSections } from './ProposalBMOGif';
import { buildIncomeStreamSections } from './ProposalIncomeStream';
import { isCorrelationMatrixProposalInline, splitProposalCorrelationMatrix } from '../utils/correlation-matrix';
import { deepClone } from '../../../utils/objects';
import { buildRiskProfileSections } from './ProposalRiskProfiles';
import { buildSuitabilityDeterminationSections } from './ProposalSuitabilityDetermination';
import { buildInvestorQuestionnaire } from './ProposalInvestorQuestionnaire';
import { buildInstrumentCustomDisclaimers } from './ProposalInstrumentCustomDisclaimers';
import { buildBenchmarksBreakdownDisclaimers } from './ProposalBenchmarksBreakdownDisclaimers';
import { buildRebalanceAnalysis } from './ProposalRebalanceAnalysis';
import { validateDisableRules } from '../ProposalHelpers';
import ComparisonItemType from '@/types/comparison-item-type';
import ComparisonItem from '@/types/comparison-item';
import CorrelationMatrixData from '@/types/correlation-matrix-data';
import RiskProfileItem from '@/types/risk-profile-item';
import QuestionnaireAnswers from '@/types/questionnaire-answers';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import ProposalOmniRiskProfilesQuestionnaire from '@/types/risk-profiles/omni/proposal-omni-risk-profiles-questionnaire';
import User from '@/types/user';

/*
The code here is custom to some sections. We should try to avoid doing this as much as we can.
Should you get inspiration here, please ask yourself: should I try to generalize what is shared? Would the benefit exceed the cost?
*/

export const preprocessCorrelationMatrices = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  templateOptions: ProposalNS.ProposalTemplateOptions,
  proposalItems: ComparisonItem[],
) => {
  // If correlation matrices aren't selected, return the unmodified template.
  if (selectedSections.indexOf('CORRELATION_MATRICES') === -1) {
    return template;
  }

  const templateCopy = deepClone(template);
  const {
    styleOptions: { orientation },
  } = templateOptions;

  let divisionIndex = -1;
  let sectionIndex = -1;

  for (let index = 0; index < templateCopy.root.length; index++) {
    const secIndex = templateCopy.root[index].sections.findIndex(s => s.key === 'CORRELATION_MATRICES');
    if (secIndex !== -1) {
      divisionIndex = index;
      sectionIndex = secIndex;
      break;
    }
  }

  if (divisionIndex === -1 || sectionIndex === -1) {
    return templateCopy;
  }

  const division = templateCopy.root[divisionIndex];
  const section = division.sections[sectionIndex];

  let itemTypes: [ComparisonItemType] | undefined;
  if (section.allowedItemTypes != null && section.allowedItemTypes.length > 0) {
    itemTypes = section.allowedItemTypes as [ComparisonItemType];
  }

  let items = proposalItems;
  if (itemTypes !== undefined) {
    items = proposalItems.filter(item => itemTypes?.includes(item.type));
  }

  // test disable rules
  if (section.disableRules != null && section.disableRules.length > 0 && validateDisableRules(section.disableRules, selectedSections, items).isDisabled) {
    return templateCopy;
  }

  const inline: boolean = isCorrelationMatrixProposalInline(items, orientation);
  if (inline) {
    // If matrices fit inline-style, generate the appropriate section.
    // Correlations are too big to be added inline-style - adding them at the bottom of the proposal.
    const data: CorrelationMatrixData[] = splitProposalCorrelationMatrix(orientation, items);
    const correlationSections = buildCorrelationMatricesSections(section, data, items, templateOptions, true, itemTypes as [ProposalNS.ProposalItemType]);
    division.sections[sectionIndex] = correlationSections[0];
    return templateCopy;
  }

  // Correlations are too big to be added inline-style - adding them at the bottom of the proposal.
  const data: CorrelationMatrixData[] = splitProposalCorrelationMatrix(orientation, items);

  if (data.length > 0) {
    templateCopy.root[divisionIndex] = { ...templateCopy.root[divisionIndex] };
    const division = templateCopy.root[divisionIndex];

    division.sections = division.sections.slice();
    division.sections.splice(sectionIndex, 1);

    // add to the end
    const bottomIndex = templateCopy.root.length - 1;
    if (bottomIndex !== -1) {
      templateCopy.root[bottomIndex] = { ...templateCopy.root[bottomIndex] };
      const bottomDivision = templateCopy.root[bottomIndex];
      bottomDivision.sections = bottomDivision.sections.slice();

      const newSections = buildCorrelationMatricesSections(section, data, items, templateOptions, false, itemTypes as [ProposalNS.ProposalItemType]);
      bottomDivision.sections = [...newSections, ...bottomDivision.sections];
    }
  }

  return templateCopy;
};

export const preprocessBMOGif = (
  selectedSections: string[],
  proposalGifItems: BMOGIFItem[],
  template: ProposalNS.ProposalTemplateV2,
  gifHideBaseScenario: boolean,
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
) => {
  // is the section on, if not bail out
  if (selectedSections.indexOf('BMO_GIF_SUMMARY') === -1 || proposalGifItems.length === 0) {
    return template;
  }

  const templateCopy = deepClone(template);

  templateCopy.root = templateCopy.root.slice();
  const index = templateCopy.root.findIndex(div => div.key === 'BOTTOM');

  if (index !== -1) {
    templateCopy.root[index] = { ...templateCopy.root[index] };
    const division = templateCopy.root[index];

    const sectionIndex = division.sections.findIndex(section => section.key === 'BMO_GIF_SUMMARY');
    const hideBaseScenario: boolean = gifHideBaseScenario !== undefined ? gifHideBaseScenario : false;
    const newSections = buildBmoGifSections(proposalGifItems[0], proposalTemplateOptions.locale, hideBaseScenario);

    // Insert new sections directly after the BMO_GIF_SUMMARY section
    division.sections = [
      ...division.sections.slice(0, sectionIndex + 1), // Before BMO_GIF_SUMMARY
      ...newSections, // New sections
      ...division.sections.slice(sectionIndex + 1), // After BMO_GIF_SUMMARY
    ];
  }

  return templateCopy;
};

export const preprocessIncomeStream = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  proposalItems: ComparisonItem[],
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
  showBaseCurrencies?: boolean,
) => {
  // is the section on, if not bail out
  if (selectedSections.indexOf('INCOME_STREAM') === -1) {
    return template;
  }

  const templateCopy = deepClone(template);

  templateCopy.root = templateCopy.root.slice();
  const index = templateCopy.root.findIndex(div => div.key === 'MAIN');

  if (index !== -1) {
    templateCopy.root[index] = { ...templateCopy.root[index] };
    const division = templateCopy.root[index];

    const newSections = buildIncomeStreamSections(
      proposalItems,
      proposalTemplateOptions.styleOptions.orientation,
      proposalTemplateOptions.locale,
      template.customParameters?.singleItemShift === true && proposalItems.length === 1,
      showBaseCurrencies,
    );
    const insertIndex = division.sections.findIndex(section => section.key === 'INCOME_STREAM');

    division.sections = [...division.sections.slice(0, insertIndex), ...newSections, ...division.sections.slice(insertIndex + 1)];
  }

  return templateCopy;
};
export const preprocessRiskProfiles = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  proposalRiskProfile: RiskProfileItem | undefined = undefined,
  proposalItems: ComparisonItem[],
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
  user: User,
) => {
  if (selectedSections.indexOf('CUSTOM_RISK_PROFILE') === -1) {
    return template;
  }

  const divIndex = template.root.findIndex(div => div.sections.some(section => section.key === 'CUSTOM_RISK_PROFILE'));
  if (divIndex === -1) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];
  const insertIndex = division.sections.findIndex(section => section.key === 'CUSTOM_RISK_PROFILE');

  const newSections = buildRiskProfileSections(
    division.sections[insertIndex],
    proposalRiskProfile,
    proposalItems,
    proposalTemplateOptions.styleOptions.orientation,
    proposalTemplateOptions.locale,
    user,
  );

  division.sections.splice(insertIndex, 1, ...newSections);

  return templateCopy;
};

export const preprocessSuitabilityDetermination = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  rationaleAnswers: QuestionnaireAnswers | undefined,
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
) => {
  const suitabilitySectionKey = 'SUITABILITY_DETERMINATION';

  const divIndex = template.root.findIndex(div => div.sections.some(section => section.key === suitabilitySectionKey));
  if (divIndex === -1) {
    return template;
  }

  const insertIndex = template.root[divIndex].sections.findIndex(section => section.key === suitabilitySectionKey);
  if (insertIndex === -1) {
    return template;
  }

  const isSelected = selectedSections.indexOf(suitabilitySectionKey) > -1;
  const isForced = template.root[divIndex].sections[insertIndex].forced;
  if (!isSelected && !isForced) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];

  const newSections = buildSuitabilityDeterminationSections(
    division.sections[insertIndex],
    rationaleAnswers,
    proposalTemplateOptions.styleOptions.orientation,
    proposalTemplateOptions.locale,
  );

  division.sections.splice(insertIndex, 1, ...newSections);

  return templateCopy;
};

export const preprocessInvestorQuestionnaire = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
  questionnaire?: KycQuestionnaire | ProposalOmniRiskProfilesQuestionnaire,
  riskProfile?: RiskProfileItem,
) => {
  if (!riskProfile?.savedSections || !questionnaire) {
    return template;
  }

  const investorQuestionnaireSectionKey = 'INVESTOR_QUESTIONNAIRE';

  const divIndex = template.root.findIndex(div => div.sections.some(section => section.key === investorQuestionnaireSectionKey));
  if (divIndex === -1) {
    return template;
  }

  const insertIndex = template.root[divIndex].sections.findIndex(section => section.key === investorQuestionnaireSectionKey);
  if (insertIndex === -1) {
    return template;
  }

  const isSelected = selectedSections.indexOf(investorQuestionnaireSectionKey) > -1;
  const isForced = template.root[divIndex].sections[insertIndex].forced;
  if (!isSelected && !isForced) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];

  const newSections = buildInvestorQuestionnaire(
    division.sections[insertIndex],
    riskProfile,
    questionnaire,
    proposalTemplateOptions.styleOptions.orientation,
    proposalTemplateOptions.locale,
  );

  division.sections.splice(insertIndex, 1, ...newSections);

  return templateCopy;
};

export const preprocessInstrumentCustomDisclaimers = (
  template: ProposalNS.ProposalTemplateV2,
  templateOptions: ProposalNS.ProposalTemplateOptions,
  proposalItems: ComparisonItem[],
) => {
  const divIndex = template.root.findIndex(div => div.key === 'BOTTOM' || div.key === 'COMMENTARY' || div.key === 'DISCLAIMERS');

  if (divIndex === -1) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];
  const newSections = buildInstrumentCustomDisclaimers(proposalItems, templateOptions.styleOptions.orientation, templateOptions.locale, template.key);
  division.sections = [...division.sections, ...newSections];
  return templateCopy;
};

export const preprocessBenchmarksBreakdownDisclaimers = (
  template: ProposalNS.ProposalTemplateV2,
  templateOptions: ProposalNS.ProposalTemplateOptions,
  proposalItems: ComparisonItem[],
) => {
  const divIndex = template.root.findIndex(div => div.key === 'DISCLAIMERS');
  if (divIndex === -1) {
    return template;
  }

  const { benchmarksBreakdownDisclaimers } = template;
  if (benchmarksBreakdownDisclaimers == null) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];

  const newSections = buildBenchmarksBreakdownDisclaimers(
    proposalItems,
    benchmarksBreakdownDisclaimers,
    templateOptions.styleOptions.orientation,
    templateOptions.locale,
  );

  division.sections = [...newSections, ...division.sections];

  return templateCopy;
};

export const preprocessRebalanceAnalysis = (
  selectedSections: string[],
  template: ProposalNS.ProposalTemplateV2,
  proposalItems: ComparisonItem[],
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions,
) => {
  const REBALANCE_ANALYSIS_KEY = 'REBALANCE_ANALYSIS';

  if (selectedSections.indexOf(REBALANCE_ANALYSIS_KEY) === -1) {
    return template;
  }

  const divIndex = template.root.findIndex(div => div.sections.some(section => section.key === REBALANCE_ANALYSIS_KEY));
  if (divIndex === -1) {
    return template;
  }

  const templateCopy = deepClone(template);
  const division = templateCopy.root[divIndex];
  const sectionIndex = division.sections.findIndex(section => section.key === REBALANCE_ANALYSIS_KEY);

  const newSections = buildRebalanceAnalysis(
    division.sections[sectionIndex],
    proposalItems,
    proposalTemplateOptions.styleOptions.orientation,
    proposalTemplateOptions.locale,
  );

  division.sections.splice(sectionIndex, 1, ...newSections);

  return templateCopy;
};
